/**
 * The native-ECMAScript-error classification primitive, shared by every producer that
 * maps a caught/rejected value onto a closed `error_name` category (`render_failure`,
 * `unhandled_exception`) — extracted rather than reimplemented per type, since a second
 * hand-maintained copy of this logic is exactly the drift this contract exists to
 * prevent (see `clientTelemetryContract.ts`'s header).
 *
 * ⛔ EXACT NATIVE BRAND identity — the value's DIRECT prototype must be the native's own
 * `prototype` — under a non-throwing guard (product round 4 F8, round 6 P1c, architect
 * round 5 blocker 5). Neither readable field nor base membership defines the category:
 *  - `error.name` is never read (one assignment mints any category);
 *  - the writable `constructor` field is never read (one assignment spoofs it);
 *  - `instanceof` is not used either: it is BASE-CLASS membership, which let a custom
 *    `class AudioError extends Error {}` classify as `Error` — the frozen contract maps
 *    custom subclasses to `unknown` (application categories), so only the exact
 *    own-brand prototype matches;
 *  - a shadowed `class TypeError extends Error {}` is NOT classified `TypeError` (it is
 *    not one) — it classifies as its honest native BASE `Error`, because subclass
 *    instances really are instances of the base;
 *  - cross-realm errors fail the prototype check — `unknown`, conservative;
 *  - subclasses are tested BEFORE `Error` (`AggregateError` et al. extend it);
 *  - every access is guarded: `instanceof`/`getPrototypeOf` can throw on a hostile
 *    prototype (proxy), and classification must NEVER replace the original failure.
 */
export function classifyNativeError(error, brands) {
    try {
        if (error === null || typeof error !== 'object')
            return 'unknown';
        const proto = Object.getPrototypeOf(error);
        for (const [name, brand] of brands) {
            if (brand !== undefined && proto === brand)
                return name;
        }
        return 'unknown';
    }
    catch {
        return 'unknown';
    }
}
/**
 * Resolves each brand NAME to its native prototype off `globalThis`, in the ORDER given
 * (subclasses-before-base matters — `classifyNativeError` returns the first match). The
 * lib target predates ES2021, so on an engine missing one of the names (e.g. no
 * `SuppressedError`) that entry's brand is `undefined` and is skipped by
 * `classifyNativeError`'s `brand !== undefined` guard — conservative: those values map to
 * `unknown` rather than throwing.
 */
export function nativeErrorBrandsFor(names) {
    return names.map((name) => [
        name,
        globalThis[name]
            ?.prototype,
    ]);
}
//# sourceMappingURL=nativeErrorBrand.js.map